const ALLOWED_TAGS = new Set([
  "H1",
  "H2",
  "H3",
  "H4",
  "P",
  "UL",
  "OL",
  "LI",
  "STRONG",
  "EM",
  "B",
  "I",
  "A",
  "BR",
  "BLOCKQUOTE"
]);

const escapeHtml = text =>
  String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const sanitizeUrl = url => {
  const trimmed = String(url || "").trim();
  if (!trimmed || /^javascript:/i.test(trimmed) || /^data:/i.test(trimmed)) {
    return null;
  }

  if (
    /^https?:\/\//i.test(trimmed) ||
    trimmed.startsWith("/") ||
    trimmed.startsWith("#")
  ) {
    return trimmed;
  }

  return null;
};

const inlineMarkdown = text => {
  let result = escapeHtml(text);
  result = result.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, label, url) => {
    const safeUrl = sanitizeUrl(url);
    if (!safeUrl) {
      return label;
    }

    return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${label}</a>`;
  });
  result = result.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  result = result.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  return result;
};

export const markdownToHtml = markdown => {
  if (!markdown?.trim()) {
    return "";
  }

  const lines = markdown.replace(/\r\n/g, "\n").split("\n");
  let html = "";
  let inList = false;
  let listType = null;

  const closeList = () => {
    if (inList) {
      html += listType === "ol" ? "</ol>" : "</ul>";
      inList = false;
      listType = null;
    }
  };

  lines.forEach(line => {
    const trimmed = line.trim();

    if (!trimmed) {
      closeList();
      return;
    }

    const headingMatch = trimmed.match(/^(#{1,3})\s+(.+)$/);
    if (headingMatch) {
      closeList();
      const level = headingMatch[1].length;
      html += `<h${level}>${inlineMarkdown(headingMatch[2])}</h${level}>`;
      return;
    }

    const ulMatch = trimmed.match(/^[-*]\s+(.+)$/);
    if (ulMatch) {
      if (!inList || listType !== "ul") {
        closeList();
        html += "<ul>";
        inList = true;
        listType = "ul";
      }
      html += `<li>${inlineMarkdown(ulMatch[1])}</li>`;
      return;
    }

    const olMatch = trimmed.match(/^\d+\.\s+(.+)$/);
    if (olMatch) {
      if (!inList || listType !== "ol") {
        closeList();
        html += "<ol>";
        inList = true;
        listType = "ol";
      }
      html += `<li>${inlineMarkdown(olMatch[1])}</li>`;
      return;
    }

    closeList();
    html += `<p>${inlineMarkdown(trimmed)}</p>`;
  });

  closeList();
  return html;
};

const stripUnsafeHtml = html =>
  String(html || "")
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
    .replace(/\son\w+="[^"]*"/gi, "")
    .replace(/\son\w+='[^']*'/gi, "")
    .replace(/href\s*=\s*["']javascript:[^"']*["']/gi, "");

export const sanitizeHtml = html => {
  if (!html?.trim()) {
    return "";
  }

  if (typeof DOMParser === "undefined") {
    return stripUnsafeHtml(html);
  }

  const doc = new DOMParser().parseFromString(html, "text/html");

  const walk = node => {
    [...node.childNodes].forEach(child => {
      if (child.nodeType !== Node.ELEMENT_NODE) {
        return;
      }

      // Internal-only inline annotations (<vi>…</vi>) are kept as a marked span
      // so internal readers can tell the text is not shown to the customer.
      // For the customer audience these segments are already stripped
      // server-side, so they never reach this point.
      if (child.tagName === "VI") {
        const marker = doc.createElement("span");
        marker.className = "vi";
        while (child.firstChild) {
          marker.appendChild(child.firstChild);
        }
        child.replaceWith(marker);
        walk(marker);
        return;
      }

      if (!ALLOWED_TAGS.has(child.tagName)) {
        const fragment = doc.createDocumentFragment();
        while (child.firstChild) {
          fragment.appendChild(child.firstChild);
        }
        child.replaceWith(fragment);
        walk(fragment);
        return;
      }

      [...child.attributes].forEach(attr => {
        if (
          attr.name.startsWith("on") ||
          (attr.name === "href" && /^javascript:/i.test(attr.value))
        ) {
          child.removeAttribute(attr.name);
        }
      });

      if (child.tagName === "A") {
        child.setAttribute("rel", "noopener noreferrer");
        child.setAttribute("target", "_blank");
      }

      walk(child);
    });
  };

  walk(doc.body);
  return doc.body.innerHTML;
};

export const resolveArtifactBodyHtml = ({ bodyHtml, bodyMd }) => {
  if (bodyHtml?.trim()) {
    return sanitizeHtml(bodyHtml);
  }

  if (bodyMd?.trim()) {
    return sanitizeHtml(markdownToHtml(bodyMd));
  }

  return "";
};
